import { useState, useEffect } from 'react';
import { lockScroll, unlockScroll } from 'utils/scrollLock';

// Chevron SVG minimalista
const Chevron = ({ isOpen }) => (
  <svg
    width="12"
    height="7"
    viewBox="0 0 12 7"
    fill="none"
    className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
  >
    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Checkbox custom
const FilterCheckbox = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer group py-1">
    <div className="relative w-[18px] h-[18px] flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div className={`
        w-[18px] h-[18px] border transition-all
        ${checked
          ? 'border-[var(--phys-ink)] bg-[var(--phys-ink)]'
          : 'border-[#ccc] bg-white group-hover:border-[var(--phys-slate)]'
        }
      `}>
        {checked && (
          <svg className="w-full h-full p-[3px]" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M4 10L8 14L16 6" />
          </svg>
        )}
      </div>
    </div>
    <span className="text-[13px] font-light text-[var(--phys-ink)]">{label}</span>
  </label>
);

// Accordion section
const FilterSection = ({ title, options, selected, onToggle, isOpen, onToggleOpen }) => {
  return (
    <div className="border-b border-[#e0e0dc]">
      {/* Header */}
      <button
        onClick={onToggleOpen}
        className="flex items-center justify-between w-full py-5 text-left"
      >
        <span className="text-[15px] font-normal tracking-wide text-[var(--phys-ink)]">{title}</span>
        <Chevron isOpen={isOpen} />
      </button>

      {/* Selected tags (shown when section is closed) */}
      {!isOpen && selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-4 -mt-1">
          {selected.map(item => (
            <span
              key={item}
              className="inline-flex items-center gap-1 text-[12px] text-[var(--phys-slate)] bg-[#e8e8e4] px-2.5 py-1"
            >
              {item}
              <button
                onClick={(e) => { e.stopPropagation(); onToggle(item); }}
                className="ml-0.5 text-[var(--phys-slate)] hover:text-[var(--phys-ink)] text-[14px] leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Expandable content */}
      <div
        style={{ maxHeight: isOpen ? '400px' : '0' }}
        className="overflow-hidden transition-all duration-300 ease-in-out"
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 pb-5">
          {options.map((option) => (
            <FilterCheckbox
              key={option}
              checked={selected.includes(option)}
              onChange={() => onToggle(option)}
              label={option}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function ProductFilters({ isOpen, onClose, onApplyFilters, availableTags = {}, activeFilters = {} }) {
  const [selectedSkinTypes, setSelectedSkinTypes] = useState(activeFilters.skinTypes || []);
  const [selectedConcerns, setSelectedConcerns] = useState(activeFilters.concerns || []);
  const [selectedIngredients, setSelectedIngredients] = useState(activeFilters.ingredients || []);
  const [isAnimating, setIsAnimating] = useState(false);
  const [openSections, setOpenSections] = useState({});

  // Sync when filters change from URL
  useEffect(() => {
    setSelectedSkinTypes(activeFilters.skinTypes || []);
    setSelectedConcerns(activeFilters.concerns || []);
    setSelectedIngredients(activeFilters.ingredients || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [isOpen]);

  const skinTypes   = availableTags.skinTypes   || [];
  const concerns    = availableTags.concerns    || [];
  const ingredients = availableTags.ingredients || [];

  const toggleOption = (option, list, setList) => {
    if (list.includes(option)) {
      setList(list.filter(item => item !== option));
    } else {
      setList([...list, option]);
    }
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const clearFilters = () => {
    setSelectedSkinTypes([]);
    setSelectedConcerns([]);
    setSelectedIngredients([]);
    onApplyFilters({ skinTypes: [], concerns: [], ingredients: [] });
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => { onClose(); }, 300);
  };

  const applyFilters = () => {
    onApplyFilters({
      skinTypes: selectedSkinTypes,
      concerns: selectedConcerns,
      ingredients: selectedIngredients,
    });
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`
          fixed inset-0 bg-black/20 z-[9998] backdrop-blur-sm
          transition-opacity duration-300
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
        `}
        onClick={handleClose}
      />

      {/* Filters Panel */}
      <div
        className={`
          fixed right-0 top-0 bottom-0 w-full max-w-lg bg-[var(--phys-snow)] z-[9999]
          overflow-y-auto shadow-2xl
          transition-transform duration-300 ease-in-out
          ${isAnimating ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="sticky top-0 bg-[var(--phys-snow)] border-b border-[#e0e0dc] px-8 py-5 flex items-center justify-between z-10">
            <h2 className="text-[18px] tracking-wide font-light">Filtros</h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center hover:opacity-60 transition-opacity"
              aria-label="Close filters"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1"/>
              </svg>
            </button>
          </div>

          {/* Filter Sections */}
          <div className="flex-1 px-8 pt-2">
            <FilterSection
              title="Tipo de Piel"
              options={skinTypes}
              selected={selectedSkinTypes}
              onToggle={(opt) => toggleOption(opt, selectedSkinTypes, setSelectedSkinTypes)}
              isOpen={!!openSections.skinTypes}
              onToggleOpen={() => toggleSection('skinTypes')}
            />
            <FilterSection
              title="Ingredientes"
              options={ingredients}
              selected={selectedIngredients}
              onToggle={(opt) => toggleOption(opt, selectedIngredients, setSelectedIngredients)}
              isOpen={!!openSections.ingredients}
              onToggleOpen={() => toggleSection('ingredients')}
            />
            <FilterSection
              title="Por beneficio"
              options={concerns}
              selected={selectedConcerns}
              onToggle={(opt) => toggleOption(opt, selectedConcerns, setSelectedConcerns)}
              isOpen={!!openSections.concerns}
              onToggleOpen={() => toggleSection('concerns')}
            />
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-[var(--phys-snow)] border-t border-[#e0e0dc] px-8 py-5 flex gap-4 z-10">
            <button
              onClick={applyFilters}
              className="flex-1 py-3.5 bg-[var(--phys-ink)] text-white text-[12px] tracking-widest uppercase font-light hover:bg-[#333] transition-colors"
            >
              Aplicar filtros
            </button>
            <button
              onClick={clearFilters}
              className="flex-1 py-3.5 border border-[#ccc] text-[12px] tracking-widest uppercase font-light text-[var(--phys-slate)] hover:border-[var(--phys-ink)] hover:text-[var(--phys-ink)] transition-colors"
            >
              Eliminar filtros
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
